import { Editor, Element as SlateElement, Transforms } from 'slate';
import type { CustomElement } from './slate-types';

/** Ensure editor.selection points at an existing text node (never inside a void image). */
export function normalizeEditorSelection(editor: Editor): void {
  const { selection } = editor;
  if (!selection) {
    try {
      Transforms.select(editor, Editor.end(editor, []));
    } catch {
      try {
        Transforms.select(editor, { path: [0, 0], offset: 0 });
      } catch {
        /**/
      }
    }
    return;
  }
  try {
    const anchor = Editor.node(editor, selection.anchor.path)[0];
    const focus = Editor.node(editor, selection.focus.path)[0];
    if (SlateElement.isElement(anchor) && (anchor as CustomElement).type === 'image') {
      throw new Error('selection on image');
    }
    if (SlateElement.isElement(focus) && (focus as CustomElement).type === 'image') {
      throw new Error('selection on image');
    }
    Editor.start(editor, selection.anchor.path);
    Editor.start(editor, selection.focus.path);
    return;
  } catch {
    /** fall through */
  }
  try {
    Transforms.select(editor, Editor.end(editor, []));
  } catch {
    try {
      Transforms.select(editor, { path: [0, 0], offset: 0 });
    } catch {
      /**/
    }
  }
}

/**
 * Insert a logo/image as its own paragraph plus a trailing empty line for the cursor.
 * Avoids inline void selection paths that crash the toolbar on the next render.
 */
export function insertEmailImage(editor: Editor, url: string, alt = ''): void {
  normalizeEditorSelection(editor);
  const imageNode: CustomElement = { type: 'image', url, alt, children: [{ text: '' }] };
  const imageParagraph: CustomElement = { type: 'paragraph', children: [imageNode] };
  const trailingParagraph: CustomElement = { type: 'paragraph', children: [{ text: '' }] };
  const insertAt = editor.selection?.anchor.path[0] ?? editor.children.length;
  Transforms.insertNodes(editor, [imageParagraph, trailingParagraph]);
  try {
    const trailingIndex = Math.min(insertAt + 2, editor.children.length - 1);
    Transforms.select(editor, Editor.start(editor, [trailingIndex]));
  } catch {
    normalizeEditorSelection(editor);
  }
}
